import React, { useState, useEffect } from 'react';
import { Segmented, Card, Typography, Row, Col, Statistic, message, Spin } from 'antd';
import { UserOutlined, AppstoreOutlined, LockOutlined, UserAddOutlined, IdcardOutlined } from '@ant-design/icons';
import ownerService from '@/services/owner.service';
import OwnerUserManagement from './OwnerUserManagement';
import OwnerShipsManagement from './OwnerShipsManagement';
import { useDispatch, useSelector } from 'react-redux';
import UserProfile from '../user/UserProfile';
import ScrollableSegmented from '@/components/SpotSegmented';

const { Title, Text } = Typography;

const RootOwnerDashboard = () => {
    const [activeTab, setActiveTab] = useState('profile');
    const [ships, setShips] = useState([]);
    const [users, setUsers] = useState([]);
    const [vesselTypes, setVesselTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch()
    // Mock vessel types
    const { subscription } = useSelector(state => state.auth)

    const mockVesselTypes = [
        'Container Ship',
        'Bulk Carrier',
        'Tanker',
        'General Cargo',
        'Oil Tanker',
        'Chemical Tanker',
        'LNG Carrier',
        'Ro-Ro Ship'
    ];

    useEffect(() => {
        setVesselTypes(mockVesselTypes);
        loadDashboardData();
    }, []);

    // Shared API function to load all dashboard data
    const loadDashboardData = async () => {
        setLoading(true);
        try {
            await Promise.all([
                loadShips(),
                loadUsers()
            ]);
        } catch (error) {
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    // Load Ships for Owner
    const loadShips = async () => {
        try {
            const response = await ownerService.getownerships();
            if (response.success) {
                setShips(response.data || []);
            } else {
                setShips([]);
            }
        } catch (error) {
            toast.error('Failed to load ships');
            setShips([]);
        }
    };

    // Load Users for Owner
    const loadUsers = async () => {
        try {
            const response = await ownerService.getowneruser();
            if (response.success) {
                setUsers(response.data?.users || []);
            } else {
                setUsers([]);
            }
        } catch (error) {
            toast.error('Failed to load users');
            setUsers([]);
        }
    };

    // Shared context object to pass to child components
    const sharedContext = {
        ships,
        users,
        vesselTypes,
        loading,
        loadShips,
        loadUsers,
        loadDashboardData
    };

    const segmentOptions = [
        {
            label: (
                <div className="flex items-center gap-2">
                    <AppstoreOutlined />
                    <span>Ships</span>
                </div>
            ),
            value: 'ships'
        },
        {
            label: (
                <div className="flex items-center gap-2">
                    <UserOutlined />
                    <span>Users</span>
                </div>
            ),
            value: 'users'
        },
        {
            label: (
                <div className="flex items-center gap-2">
                    <IdcardOutlined />
                    <span>Profile</span>
                </div>
            ),
            value: 'profile'
        },
        {
            label: (
                <div className="flex items-center gap-2">
                    <LockOutlined />
                    <span>Password</span>
                </div>
            ),
            value: 'password'
        },
        {
            label: (
                <div className="flex items-center gap-2">
                    <UserAddOutlined />
                    <span>Referral</span>
                </div>
            ),
            value: 'refer'
        }
    ];

    return (
        <div className="min-h-screen  px-4 pt-2 bg-gray-50   ">
            <div className="mx-auto">
                {/* Header */}
                <div className='flex justify-between '>
                    <Title level={5} className=" text-gray-800  ">
                        Dashboard
                    </Title>
                </div>

                {/* Statistics Cards */}
                <Row gutter={[24, 24]} className="mb-6 mt-3">
                    <Col xs={8} sm={8} lg={3}>
                        <Card
                            className="text-center shadow-sm hover:shadow-md transition-shadow p-2"
                            bodyStyle={{ padding: '6px' }}
                        >
                            <Statistic
                                title={<span style={{ fontSize: '0.75rem', color: '#666' }}>Total Ships</span>}
                                value={ships.length}
                                valueStyle={{ color: '#1890ff', fontSize: '1.2rem', fontWeight: 600 }}
                            />

                        </Card>
                    </Col>
                    <Col xs={8} sm={8} lg={3}>

                        <Card
                            className="text-center shadow-sm hover:shadow-md transition-shadow p-2"
                            bodyStyle={{ padding: '6px' }}
                        >
                            <Statistic
                                title={<span style={{ fontSize: '0.75rem', color: '#666' }}>Total Users</span>}
                                value={users.length}
                                valueStyle={{ color: '#52c41a', fontSize: '1.2rem', fontWeight: 600 }}
                            />

                        </Card>



                    </Col>

                    <Col xs={8} sm={8} lg={3}>
                        <Card
                            className="text-center shadow-sm hover:shadow-md transition-shadow p-2"
                            bodyStyle={{ padding: '6px' }}
                        >
                            <Statistic
                                title={<span style={{ fontSize: '0.75rem', color: '#666' }}>Remaining Ships</span>}
                                value={subscription?.max_ships}
                                valueStyle={{ color: '#722ed1', fontSize: '1.2rem', fontWeight: 600 }}
                            />

                        </Card>
                    </Col>
                    <Col xs={8} sm={8} lg={3}>
                        <Card
                            className="text-center shadow-sm hover:shadow-md transition-shadow p-2"
                            bodyStyle={{ padding: '6px' }}
                        >
                            <Statistic
                                title={<span style={{ fontSize: '0.75rem', color: '#666' }}>Remaining Users</span>}
                                value={subscription?.max_users}
                                valueStyle={{ color: '#722ed1', fontSize: '1.2rem', fontWeight: 600 }}
                            />

                        </Card>
                    </Col>
                    <Col xs={8} sm={8} lg={3}>
                        <Card
                            className="text-center shadow-sm hover:shadow-md transition-shadow p-2"
                            bodyStyle={{ padding: '6px' }}
                        >
                            <Statistic
                                title={<span style={{ fontSize: '0.75rem', color: '#666' }}>Remaining Reports</span>}
                                value={subscription?.no_of_reports}
                                valueStyle={{ color: '#722ed1', fontSize: '1.2rem', fontWeight: 600 }}
                            />

                        </Card>
                    </Col>

                    <Col xs={8} sm={8} lg={3}>
                        <Card
                            className="text-center shadow-sm hover:shadow-md transition-shadow p-2"
                            bodyStyle={{ padding: '6px' }}
                        >
                            <Statistic
                                title={<span style={{ fontSize: '0.75rem', color: '#666' }}>Remaining Inspection</span>}
                                value={subscription?.no_of_inspection_reports}
                                valueStyle={{ color: '#722ed1', fontSize: '1.2rem', fontWeight: 600 }}
                            />

                        </Card>
                    </Col>

                </Row>
                <div className='flex justify-center'>
                    {/* Tab Navigation */}
                    <ScrollableSegmented
                        options={segmentOptions}
                        setActiveTab={setActiveTab}

                        activeTab={activeTab} /></div>

                {/* Tab Content */}
                <Spin spinning={loading}>
                    <div className="min-h-[500px] px-4 ">
                        {activeTab === 'ships' && (
                            <OwnerShipsManagement sharedContext={sharedContext} subscription={subscription} />
                        )}
                        {activeTab === 'users' && (
                            <OwnerUserManagement sharedContext={sharedContext} subscription={subscription} />
                        )}
                        {(activeTab == 'profile' || activeTab == 'password' || activeTab == 'refer') && (
                            <UserProfile subscription={subscription} menuname={activeTab} />
                        )}
                    </div>
                </Spin>
            </div>
        </div>
    );
};

export default RootOwnerDashboard;